import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clock, Feather } from 'lucide-react';
import { EmptyState, ErrorState, Loading } from '../../components/StatusStates';
import PostCard from '../../components/PostCard';
import ProjectCard from '../../components/ProjectCard';
import { usePosts, useProjects } from '../../hooks/use-public';
import { formatDateChinese } from '../../lib/format';
import { useSeo } from '../../lib/seo';
import { assetUrl } from '../../api/client';
import '../../styles/public.css';

export default function Home() {
  useSeo({
    title: '铁头功的博客',
    description: '铁头功的博客——记录工程项目、系统设计与技术思考。',
  });

  const postsQuery = usePosts({ page: 1, pageSize: 10 });
  const projectsQuery = useProjects();

  const posts = useMemo(() => postsQuery.data?.items ?? [], [postsQuery.data]);
  const featuredPost = posts[0] ?? null;
  const latestPosts = posts.slice(1, 6);
  const featuredProjects = useMemo(
    () => (projectsQuery.data ?? []).filter((item) => item.featured).slice(0, 3),
    [projectsQuery.data],
  );

  const allTags = useMemo(
    () => Array.from(new Set(posts.flatMap((post) => post.tags))),
    [posts],
  );

  return (
    <>
      {/* 首屏：推荐文章 */}
      <section className="recommend">
        <div className="container">
          <div className="recommend-head fade-up">
            <p className="recommend-eyebrow">推荐阅读</p>
            <h1 className="recommend-title">最新的工程实践与思考</h1>
          </div>

          {postsQuery.isLoading ? (
            <Loading />
          ) : postsQuery.isError ? (
            <ErrorState onRetry={() => postsQuery.refetch()} />
          ) : !featuredPost ? (
            <EmptyState title="暂无已发布文章" description="新的文章完成后将在这里出现。" />
          ) : (
            <>
              <Link to={`/blog/${featuredPost.slug}`} className="featured-post fade-up">
                {featuredPost.coverImage ? (
                  <div className="featured-post-media">
                    <img
                      src={assetUrl(featuredPost.coverImage) ?? undefined}
                      alt={featuredPost.title}
                      loading="eager"
                    />
                  </div>
                ) : (
                  <div className="featured-post-media featured-post-graphic" aria-hidden="true">
                    <Feather size={64} strokeWidth={1.2} />
                  </div>
                )}
                <div className="featured-post-body">
                  <div className="tag-row" style={{ marginBottom: '0.9rem' }}>
                    {featuredPost.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="tag tag-soft">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="featured-post-title">{featuredPost.title}</h2>
                  {featuredPost.excerpt && (
                    <p className="featured-post-excerpt">{featuredPost.excerpt}</p>
                  )}
                  <div className="post-meta" style={{ marginBottom: '1.25rem' }}>
                    <time dateTime={featuredPost.publishedAt ?? undefined}>
                      {formatDateChinese(featuredPost.publishedAt)}
                    </time>
                    <span className="post-meta-dot" aria-hidden="true" />
                    <span className="icon-inline">
                      <Clock size={12} aria-hidden="true" />
                      约 {featuredPost.readingTime} 分钟读完
                    </span>
                  </div>
                  <span className="featured-post-cta">
                    阅读全文
                    <ArrowRight size={15} aria-hidden="true" />
                  </span>
                </div>
              </Link>

              <div className="home-grid">
                <div>
                  <div className="section-head" style={{ marginTop: '3.5rem' }}>
                    <h2 className="section-title" style={{ fontSize: 'var(--font-size-xl)' }}>
                      最新文章
                    </h2>
                    <Link to="/blog" className="section-more">
                      全部文章
                      <ArrowUpRight size={13} style={{ verticalAlign: '-2px' }} aria-hidden="true" />
                    </Link>
                  </div>
                  {latestPosts.length === 0 ? (
                    <p className="form-hint" style={{ padding: '1rem 0' }}>
                      更多文章正在撰写中。
                    </p>
                  ) : (
                    <div className="post-list">
                      {latestPosts.map((post) => (
                        <PostCard key={post.id} post={post} />
                      ))}
                    </div>
                  )}
                </div>

                <aside className="blog-aside">
                  <div className="aside-card">
                    <p className="aside-heading">标签</p>
                    {allTags.length === 0 ? (
                      <p className="form-hint">暂无标签</p>
                    ) : (
                      <div className="tag-cloud">
                        <Link to="/blog" className="tag tag-clickable">
                          全部
                        </Link>
                        {allTags.map((tag) => (
                          <Link
                            key={tag}
                            to={`/blog?tag=${encodeURIComponent(tag)}`}
                            className="tag tag-clickable"
                          >
                            {tag}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </section>

      {/* 精选项目 */}
      <section className="home-section home-section-alt">
        <div className="container">
          <div className="section-head">
            <div>
              <h2 className="section-title">精选项目</h2>
              <p className="section-subtitle">持续建设中的代表性作品与实践</p>
            </div>
            <Link to="/projects" className="section-more">
              全部项目
              <ArrowUpRight size={13} style={{ verticalAlign: '-2px' }} aria-hidden="true" />
            </Link>
          </div>

          {projectsQuery.isLoading ? (
            <Loading />
          ) : projectsQuery.isError ? (
            <ErrorState onRetry={() => projectsQuery.refetch()} />
          ) : featuredProjects.length === 0 ? (
            <EmptyState title="暂无精选项目" description="项目内容将在整理后展示。" />
          ) : (
            <div className="card-grid">
              {featuredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
